/// <amd-module name="Test/TestSummary/Components/InProgressSummaryView" />
import { BuildStatus, DefinitionType } from 'Build/Client/Contracts/Build';
import * as React from 'react';
import { ITestAnalyticsLinkData } from 'Test/Analytics/Common/Types';
import * as Common from 'Test/common-util/Common';
import * as Telemetry from 'Test/common-util/Telemetry';
import * as TelemetryConstants from 'Test/common-util/TelemetryConstants';
import { DonutChartLegendComponent, IDonutChartLegendProps } from 'Test/TestSummary/Components/DonutChartLegend';
import { ResultSummaryColoredNumberChartComponent } from 'Test/TestSummary/Components/ResultSummaryColoredNumberChart';
import { DataType, ResultSummaryNumberChartComponent } from 'Test/TestSummary/Components/ResultSummaryDetailsChart';
import { getTestAnalyticsLinkData } from 'Test/TestSummary/Components/TestAnalyticsLink';
import { ISummaryViewState } from 'Test/TestSummary/Report.types';
import * as Resources from 'Test/TestSummary/Resources';
import { SummaryViewStore } from 'Test/TestSummary/Store/SummaryViewStore';
import { FPSLinkComponent } from 'VSS/Platform/Components/FPSLink';
import { isFeatureFlagEnabled } from 'VSS/Platform/Feature';
import { IVssComponentContext, VssComponent, WrappedComponent } from 'VSS/Platform/Layout';
import { Icon } from 'VSSUI/Icon';

export interface IInProgressSumaryProps {
    store: SummaryViewStore;
    artifactData: Common.IViewContextData;
    telemetryPropertiesForInProductLink: { [key: string]: any };
    isBuildTimeline?: boolean;
}

export class InProgressSummaryView extends VssComponent<IInProgressSumaryProps, ISummaryViewState, IVssComponentContext> {
    private _currentState: ISummaryViewState;

    constructor(props: IInProgressSumaryProps) {
        super(props);
        this.state = {} as ISummaryViewState;
    }

    public componentDidMount() {
        getTestAnalyticsLinkData(this.context, this.props.artifactData.viewContext, this.props.store.getDefinitionId()).then((testAnalyticsLinkData: ITestAnalyticsLinkData) => {
            this._currentState.testAnalyticsLinkData = testAnalyticsLinkData;
            this.setState(this._currentState);
        });
        this._logTelemetryForClick();
    }

    public render(): JSX.Element {
        if (this.props.store) {
            this._currentState = this.props.store.getState();
        }
        return this._getInProgressSummaryViewBody();
    }

    private _logTelemetryForClick() {
        let telemetryFeatureName: string = this.props.isBuildTimeline ? TelemetryConstants.featureTestTab_summaryInBuildTimelineRendered : TelemetryConstants.featureTestTab_TestTabClicked;
        if (this._currentState.aggregatedTestsCount) {
            Telemetry.publishEvent(this.context.pageContext, this.props.artifactData, telemetryFeatureName, {
                [TelemetryConstants.inProgress]: true,
                [TelemetryConstants.totalTestsExists]: (this._currentState.aggregatedTestsCount.totalTests > 0 ? "Yes" : "No"),
                [TelemetryConstants.failedTestsExists]: (this._currentState.aggregatedTestsCount.failedTests > 0 ? "Yes" : "No"),
                [TelemetryConstants.totalTests]: this._currentState.aggregatedTestsCount.totalTests,
                [TelemetryConstants.failedTests]: this._currentState.aggregatedTestsCount.failedTests,
                [TelemetryConstants.passedTests]: this._currentState.aggregatedTestsCount.passedTests
            });
        }
    }

    private _getInProgressSummaryViewBody(): JSX.Element {
        if (this._currentState && this._currentState.aggregatedRunsCount) {
            return (
                <div className="summary-view-container-parent" >
                    <div className="summary-view-container-left">
                        <div className="summary-view-bar-left">
                            <div className="summary-view-bar-icon-run-count">
                                <Icon iconName={"TestBeaker"} className="summary-view-test-icon" />
                                <div className="run-count-details">
                                    <span className="run-count-details-count">{this._currentState.aggregatedRunsCount.totalRuns}</span>
                                    <span className="run-count-details-name">{Resources.Runs}</span>
                                </div>
                            </div>
                            <div className="separator-in-bar" />
                        </div>
                        <div className="inprogress-summary-view-body margin-left-side">
                            {
                                <ResultSummaryNumberChartComponent
                                    title={Resources.TotalTestsText}
                                    value={(this._currentState.aggregatedTestsCount != null) ? this._currentState.aggregatedTestsCount.totalTests : 0}
                                    difference={null}
                                    dataType={DataType.String}
                                    isVerticalAlignWithDifferencePresent={false}
                                />
                            }
                            {
                                <div className="separator" />
                            }
                        </div>
                    </div>
                    <div className="summary-view-container-right">
                        <div className="summary-view-bar-right">
                            {
                                <div className="inprogress-run-details">
                                    <span className="inprogress-run-details-count">{this._currentState.aggregatedRunsCount.inProgressRuns}</span>
                                    <span className="inprogress-run-details-name">{Resources.ResultStateInProgress}</span>
                                </div>
                            }
                            {
                                !!this._currentState.aggregatedRunsCount.completedRuns &&
                                <div className="completed-run-details">
                                    <span className="comma">{Resources.CommaSeparator}</span>
                                    <span className="completed-run-details-count">{this._currentState.aggregatedRunsCount.completedRuns}</span>
                                    <span className="completed-run-details-name">{Resources.TestRunStateCompleted}</span>
                                </div>
                            }
                            {
                                !!this._currentState.aggregatedRunsCount.abortedRuns &&
                                <div className="aborted-run-details">
                                    <span className="comma">{Resources.CommaSeparator}</span>
                                    <span className="aborted-run-details-count" style={{ color: Common.TestReportColorPalette.Failed }}>
                                        {this._currentState.aggregatedRunsCount.abortedRuns}
                                    </span>
                                    <span className="aborted-run-details-name">{Resources.TestRunStateAborted}</span>
                                </div>
                            }
                            {this.state.testAnalyticsLinkData && this.state.testAnalyticsLinkData.testAnalyticsLinkMessage && !this.props.isBuildTimeline &&
                                <FPSLinkComponent className="failing-test-analytics-link"
                                    href={this.state.testAnalyticsLinkData.testAnalyticsLink}
                                    telemetrySource={TelemetryConstants.featureAnalyticsInProductLink_UniqueFailingTests_TestTab}
                                    telemetryProperties={this.props.telemetryPropertiesForInProductLink}>
                                    {this.state.testAnalyticsLinkData.testAnalyticsLinkMessage}
                                </FPSLinkComponent>
                            }
                            {isFeatureFlagEnabled(this.context.pageContext, Common.FeatureFlagConstants.ShowCloudTestLink) &&
                                <WrappedComponent
                                  dependencies={["ms.vss-test-web.test-cloud-test-link"]}
                                  wrappedType={"cloud-test-link"}
                                  wrappedProps={{
                                      artifactData: this.props.artifactData
                                  }}
                                />
                            }
                        </div>
                        <div className="inprogress-summary-view-body">
                            {
                                <ResultSummaryColoredNumberChartComponent
                                    title={Resources.TestOutcome_Passed}
                                    value={(this._currentState.aggregatedTestsCount != null) ? this._currentState.aggregatedTestsCount.passedTests : 0}
                                    color={Common.TestReportColorPalette.Passed}
                                />
                            }
                            {
                                <ResultSummaryColoredNumberChartComponent
                                    title={Resources.TestOutcome_Failed}
                                    value={(this._currentState.aggregatedTestsCount != null) ? this._currentState.aggregatedTestsCount.failedTests : 0}
                                    color={Common.TestReportColorPalette.Failed}
                                />
                            }
                            {
                                (this._currentState.aggregatedTestsCount != null) &&
                                !!this._currentState.aggregatedTestsCount.abortedTests &&
                                <ResultSummaryColoredNumberChartComponent
                                    title={Resources.TestOutcome_Aborted}
                                    value={(this._currentState.aggregatedTestsCount != null) ? this._currentState.aggregatedTestsCount.abortedTests : 0}
                                    color={Common.TestReportColorPalette.Aborted}
                                />
                            }
                            {
                                <div className="donut-chart-legend-container">
                                    {this._getDonutChartLegends()}
                                </div>
                            }
                        </div>
                    </div>
                </div>
            );
        }
        else {
            let build = this.props.artifactData.data.mainData;

            if (this.props.artifactData.data.mainData.status !== BuildStatus.Completed) {
                if (build.status !== BuildStatus.InProgress) {
                    return <div>{Resources.NoTestsMessageWhileBuildInProgress}</div>;
                }
            }

            let isXamlBuild = build.definition.type === DefinitionType.Xaml;
            if (!build.controller && !isXamlBuild) {
                return <div>{Resources.BuildDoesNotHaveTestReports}</div>;
            }
            if (build.status === BuildStatus.InProgress) {
                return <div>{Resources.NoTestsMessageWhileBuildInProgress}</div>;
            }
            if (build.deleted) {
                return <div> {Resources.BuildDeletedNoTestResults}</div>
            }
        }
        return (<div>{Resources.NoTestResultsMessage}</div>);
    }

    private _getDonutChartLegends(): JSX.Element {

        let donutChartLegendProps: IDonutChartLegendProps = {
            legend: []
        } as IDonutChartLegendProps;

        if (this._currentState.aggregatedTestsCount) {
            donutChartLegendProps.legend.push({
                count: this._currentState.aggregatedTestsCount.notExecutedTests,
                label: Resources.TestOutcome_NotExecuted,
                color: Common.TestReportColorPalette.NotExecuted
            });

            if (!!this._currentState.aggregatedTestsCount.notImpactedTests) {
                donutChartLegendProps.legend.push({
                    count: this._currentState.aggregatedTestsCount.notImpactedTests,
                    label: Resources.ResultStateNotImpacted,
                    color: Common.TestReportColorPalette.NotImpacted
                });
            }
            if (!!this._currentState.aggregatedTestsCount.notImpactedTests) {
                donutChartLegendProps.legend.push({
                    count: this._currentState.aggregatedTestsCount.notImpactedTests,
                    label: Resources.ResultStateNotImpacted,
                    color: Common.TestReportColorPalette.NotImpacted
                });
            }
            donutChartLegendProps.legend.push({
                count: this._currentState.aggregatedTestsCount.otherTests,
                label: Resources.OthersText,
                color: Common.TestReportColorPalette.OtherOutcome
            });
        }

        return <DonutChartLegendComponent {...donutChartLegendProps} />;
    }
}